import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AppContext';

const API = process.env.REACT_APP_BACKEND_URL;

// Emails whose visits should NOT be tracked (admins / owners). Mirrors the
// backend ADMIN_EMAILS list so the dashboard analytics aren't polluted by
// the owner browsing their own site.
const SKIP_TRACK_EMAILS = new Set([
  'cpowersv@gmail.com',
  'modtest@aivue.com',
  'admin@audivue.com',
]);

/**
 * Lightweight client-side page-view tracker.
 * Posts {path, referrer} to /api/visit on every route change.
 * Server de-dups per IP+path+hour and skips obvious bots.
 *
 * Skips tracking entirely when the logged-in user is in the admin allow-list
 * so the owner browsing their own site doesn't inflate visitor counts.
 */
export const VisitTracker = () => {
  const location = useLocation();
  const { user, token } = useAuth();

  useEffect(() => {
    // Don't record admin browsing — keeps "Visitors" analytics clean.
    if (user && user.email && SKIP_TRACK_EMAILS.has(user.email.toLowerCase())) {
      return;
    }

    // Use sendBeacon when available so the request survives page-unload
    const payload = JSON.stringify({
      path: location.pathname,
      referrer: document.referrer || null,
    });

    try {
      const url = `${API}/api/visit`;
      // When logged in, send the JWT so the backend can flag the visit as
      // belonging to a known user (excluded from "anonymous unique visitors"
      // counts). sendBeacon doesn't support custom headers, so use fetch when
      // a token is present.
      if (token) {
        fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: payload,
          keepalive: true,
        }).catch(() => { /* best-effort, non-blocking */ });
      } else if (navigator.sendBeacon) {
        const blob = new Blob([payload], { type: 'application/json' });
        navigator.sendBeacon(url, blob);
      } else {
        fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: payload,
          keepalive: true,
        }).catch(() => { /* best-effort, non-blocking */ });
      }
    } catch {
      // Tracking must never break the app
    }
  }, [location.pathname, user, token]);

  return null;
};
